import { useCallback, useEffect, useRef, useState } from "react";
import { documentService } from "../api";
import { DocumentDetailsResponse, VersionInfo } from "../interfaces/document";
import { openNotificationWithIcon } from "../utils";

export const useDocumentDetails = (documentUuid: string) => {
  const [isLoading, setIsLoading] = useState(true);
  const [currentData, setCurrentData] = useState<DocumentDetailsResponse | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<string>("");
  const [availableVersions, setAvailableVersions] = useState<VersionInfo[]>([]);
  const cacheRef = useRef<Map<string, DocumentDetailsResponse>>(new Map());

  // Initial load — fetch latest version
  useEffect(() => {
    const fetchLatest = async () => {
      try {
        const data = await documentService.getDocumentDetails(documentUuid);
        cacheRef.current.set(data.version, data);
        setCurrentData(data);
        setSelectedVersion(data.version);
        setAvailableVersions(data.availableVersions);

        // Prefetch other versions in the background
        const otherVersions = data.availableVersions.filter(
          (v) => v.version !== data.version && v.status === "COMPLETED"
        );
        otherVersions.forEach(async (v) => {
          try {
            const versionData = await documentService.getDocumentDetails(documentUuid, v.version);
            cacheRef.current.set(v.version, versionData);
          } catch {
            // Silent fail for prefetch
          }
        });
      } catch (error: any) {
        console.error("Error fetching document details:", error);
        openNotificationWithIcon("", error.response?.data?.message || error.message, "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchLatest();
  }, [documentUuid]);

  // Switch version — use cache or fetch
  const switchVersion = useCallback(async (version: string) => {
    if (version === selectedVersion) return;

    const cached = cacheRef.current.get(version);
    if (cached) {
      setCurrentData(cached);
      setSelectedVersion(version);
      return;
    }

    // Not in cache — fetch it
    setIsLoading(true);
    try {
      const data = await documentService.getDocumentDetails(documentUuid, version);
      cacheRef.current.set(version, data);
      setCurrentData(data);
      setSelectedVersion(version);
    } catch (error: any) {
      openNotificationWithIcon("", error.response?.data?.message || error.message, "error");
    } finally {
      setIsLoading(false);
    }
  }, [documentUuid, selectedVersion]);

  // Refresh sections for current version (after page range edit)
  const refreshSections = useCallback(async () => {
    try {
      const { data } = await documentService.getSectionsAndInsights(documentUuid);
      if (currentData) {
        const updated = { ...currentData, sections: data };
        setCurrentData(updated);
        cacheRef.current.set(selectedVersion, updated);
      }
    } catch (error: any) {
      openNotificationWithIcon("", error.response?.data?.message || error.message, "error");
    }
  }, [documentUuid, currentData, selectedVersion]);

  return {
    isLoading,
    currentData,
    selectedVersion,
    availableVersions,
    switchVersion,
    refreshSections,
  };
};
